import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api';
import { useGoToContact } from '../hooks/useGoToContact';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const NewsletterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const goToContact = useGoToContact();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .getNewsletter(id)
      .then((data) => {
        if (!mounted) return;
        setIssue(data);
      })
      .catch((err) => {
        if (!mounted) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!issue) return;
    const previousTitle = document.title;
    document.title = `${issue.subject} — Grouth Newsletter`;
    let metaEl = document.head.querySelector('meta[name="description"]');
    const previousDesc = metaEl?.getAttribute('content');
    if (!metaEl) {
      metaEl = document.createElement('meta');
      metaEl.setAttribute('name', 'description');
      document.head.appendChild(metaEl);
    }
    metaEl.setAttribute('content', (issue.body || '').slice(0, 158));
    return () => {
      document.title = previousTitle;
      if (previousDesc != null) metaEl.setAttribute('content', previousDesc);
    };
  }, [issue]);

  if (loading) {
    return (
      <div className="bg-[#FDFEFA] min-h-screen flex items-center justify-center pt-20">
        <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !issue) {
    return (
      <div className="bg-[#FDFEFA] min-h-screen flex items-center justify-center pt-20">
        <div className="text-center px-6">
          <h1 className="text-[#242326] text-3xl font-['Inter'] mb-3">Newsletter not found</h1>
          <p className="text-[#4A4A48] text-sm font-['Lato'] mb-5">
            This issue isn&rsquo;t in the public archive.
          </p>
          <button
            onClick={() => navigate('/newsletter')}
            className="text-[#83BF42] font-['Inter'] hover:underline"
            data-testid="newsletter-detail-back"
          >
            ← Back to all issues
          </button>
        </div>
      </div>
    );
  }

  const paragraphs = (issue.body || '').split('\n\n').filter((p) => p.trim());

  return (
    <div className="bg-[#FDFEFA] min-h-screen" data-testid="newsletter-detail-page">
      {/* Header */}
      <div className="bg-[#242326] pt-24 pb-12">
        <div className="max-w-[820px] mx-auto px-6 md:px-12">
          <button
            onClick={() => navigate('/newsletter')}
            data-testid="newsletter-detail-back-archive"
            className="inline-flex items-center gap-2 text-white/50 text-sm font-['Inter'] mb-6 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All issues
          </button>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-bold font-['Inter']">
            Newsletter
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white text-3xl md:text-5xl font-light leading-[1.1] tracking-tight font-['Inter'] mt-3"
          >
            {issue.subject}
          </motion.h1>
          <p className="text-white/50 text-xs font-['Lato'] flex items-center gap-1 mt-4">
            <Calendar className="w-3 h-3" />
            {formatDate(issue.completed_at || issue.created_at)}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[720px] mx-auto px-6 md:px-12 py-12 md:py-14">
        <div className="space-y-6">
          {paragraphs.length === 0 ? (
            <p className="text-[#4A4A48] text-[17px] leading-[1.8] font-['Lato'] font-light whitespace-pre-line">
              {issue.body}
            </p>
          ) : (
            paragraphs.map((para, idx) => (
              <p
                key={idx}
                className="text-[#4A4A48] text-[17px] leading-[1.8] font-['Lato'] font-light whitespace-pre-line"
              >
                {para}
              </p>
            ))
          )}
        </div>

        <div className="mt-10 pt-6 border-t border-[#242326]/8 flex items-center justify-between">
          <p className="text-[#B8B8B8] text-xs font-['Lato']">
            Sent to {issue.sent_count ?? issue.recipient_count ?? 0} subscribers
          </p>
          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: issue.subject, url: window.location.href });
              } else {
                navigator.clipboard?.writeText(window.location.href);
              }
            }}
            data-testid="newsletter-share"
            className="flex items-center gap-2 text-[#4A4A48] text-sm font-['Inter'] hover:text-[#83BF42] transition-colors"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        {/* CTA */}
        <div className="mt-10 bg-[#242326] rounded-2xl p-7 md:p-8">
          <h3 className="text-white text-xl md:text-2xl font-medium font-['Inter'] mb-2">
            Ready to put thinking into action?
          </h3>
          <p className="text-white/50 text-sm font-['Lato'] font-light mb-5">
            We&rsquo;re the team behind these issues. Let&rsquo;s talk.
          </p>
          <button
            onClick={goToContact}
            data-testid="newsletter-detail-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#83BF42] text-[#83BF42] text-[15px] font-['Inter'] font-light hover:bg-[#83BF42] hover:text-white transition-all duration-300 group"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsletterDetail;
